import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { type CustomChart } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { atom } from "jotai"
import { useMemo, useState } from "react"
import { CopyAsImageModal } from "../CopyAsImage/CopyAsImageModal"
import TransparentBackgroundWrapper from "../CopyAsImage/TransparentBackgroundWrapper"
import { ChartConfigSidebar } from "./ChartConfigSidebar"

import { ChartImagePreviewWrapper } from "./ChartImagePreviewWrapper"
import { useSourcesAndDefinitionsTextForChartSeries } from "./hooks/useSourcesAndDefinitionsText"
import { type ChartImageState } from "./types"

export type ChartImageModalProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  chartConfig: CustomChart
  defaultProject?: {
    project_id: string
    name: string
    logo: string
  } | null
  defaultShowSources?: boolean
  defaultShowFootnote?: boolean
  renderContent: (props: { projectLogo?: React.ReactNode }) => React.ReactNode
}

export const CopyChartAsImageModal = ({
  isOpen,
  setIsOpen,
  chartConfig,
  defaultProject,
  defaultShowSources,
  defaultShowFootnote,
  renderContent,
}: ChartImageModalProps) => {
  const defaultStyles: ChartImageState = useMemo(
    () => ({
      theme: "default",
      showBackground: true,
      background: { type: "preset", presetKey: "default" },
      aspectRatio: "16/9",
      sourcesAndFootnoteDisplay: "out-of-card",
      showSources: defaultShowSources ?? true,
      showFootnote: defaultShowFootnote ?? true,
      image: {
        type: "none",
        projectId: defaultProject?.project_id,
        projectName: defaultProject?.name,
        projectLogo: defaultProject?.logo,
      },
    }),
    [defaultProject, defaultShowSources, defaultShowFootnote]
  )
  const [state, setState] = useState<ChartImageState>(defaultStyles)

  const seriesAtom = useMemo(() => atom(chartConfig.configs), [chartConfig])
  const sourcesAndDefinitionsText = useSourcesAndDefinitionsTextForChartSeries({
    serieSettingsAtom: seriesAtom,
  })

  let image: React.ReactNode | undefined
  if (state.image?.type === "project" && state.image?.projectId) {
    image = (
      <Avatar
        src={`https://static1.tokenterminal.com/${state.image.projectLogo}`}
        className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
      />
    )
  } else if (state.image?.type === "custom" && state.image.customUrl) {
    image = (
      <Avatar
        src={state.image.customUrl}
        className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
      />
    )
  } else if (state.image?.type !== "none") {
    image = (
      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-neutral-500/20" />
    )
  }

  const padding = 60
  const dimensionsStyles: Record<string, string> = {
    width: `${900 + (state.showBackground ? padding * 2 : 0)}px`,
    height: `${500 + (state.showBackground ? padding * 2 : 0)}px`,
  }

  return (
    <CopyAsImageModal isOpen={isOpen} setIsOpen={setIsOpen}>
      {({ ref }) => (
        <>
          <TransparentBackgroundWrapper>
            <ChartImagePreviewWrapper
              innerRef={ref}
              state={state}
              sourcesAndDefinitionsText={sourcesAndDefinitionsText}
              footnoteText={chartConfig.footnote ?? undefined}
              padding={padding}
              dimensionsStyles={dimensionsStyles}
            >
              {renderContent ? renderContent({ projectLogo: image }) : null}
            </ChartImagePreviewWrapper>
          </TransparentBackgroundWrapper>
          <ChartConfigSidebar
            state={state}
            setState={setState}
            sourcesAndDefinitionsText={sourcesAndDefinitionsText}
            footnoteText={chartConfig.footnote ?? undefined}
          />
        </>
      )}
    </CopyAsImageModal>
  )
}
